// Single fetch wrapper for the whole SPA.
//
// Responsibilities:
//  - prefix every call with /api/v1 (reaches the backend through the Vite dev proxy)
//  - inject the in-memory access token as a Bearer header
//  - send credentials so the HttpOnly refresh cookie travels on /api/v1/auth/refresh
//  - unwrap the { status, data } / { status, error } response envelope
//  - implement the 401 -> single silent refresh -> retry-once flow (NO loop)
//
// Security (architecture A-Storage): the access token lives ONLY in this module's
// memory — never localStorage/sessionStorage/IndexedDB. The refresh token is an
// HttpOnly cookie the SPA can neither read nor store.

const API_PREFIX = "/api/v1";
const REFRESH_PATH = "/auth/refresh";

// --- In-memory access token (module-scoped, never persisted) -----------------

let accessToken: string | null = null;
const tokenListeners = new Set<(token: string | null) => void>();

export function getAccessToken(): string | null {
  return accessToken;
}

/** Set (or clear) the in-memory token and notify subscribers (e.g. AuthContext). */
export function setAccessToken(token: string | null): void {
  accessToken = token;
  tokenListeners.forEach((listener) => listener(token));
}

/** Subscribe to token changes. Returns an unsubscribe function. */
export function subscribeAccessToken(listener: (token: string | null) => void): () => void {
  tokenListeners.add(listener);
  return () => tokenListeners.delete(listener);
}

// --- Auth-expired handler (set by AuthContext) -------------------------------

let onAuthExpired: (() => void) | null = null;

/** Register a callback fired when the session can no longer be refreshed. */
export function setAuthExpiredHandler(handler: (() => void) | null): void {
  onAuthExpired = handler;
}

// --- Errors ------------------------------------------------------------------

/** Thrown when the backend returns a structured error envelope. */
export class ApiError extends Error {
  code: string;
  status: number;
  details?: unknown;

  constructor(message: string, code: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/** Thrown when the session is gone for good (refresh failed). Guards redirect to /sign-in. */
export class AuthExpiredError extends Error {
  constructor(message = "Session expired") {
    super(message);
    this.name = "AuthExpiredError";
  }
}

/**
 * True when an error is a genuine TRANSPORT failure — `apiFetch` wraps a fetch reject (offline, DNS,
 * CORS) as `ApiError(..., "NETWORK_ERROR", 0)`. The single offline signal the read hooks branch on:
 * `useGetEvaluation`/`useListEvaluations` serve the local IDB fallback on it, and the clients
 * surfaces render a calm offline notice instead of the red "couldn't load" banner (gh-471). A real
 * `4xx/5xx` is NOT offline — it surfaces normally (no stale-IDB masking).
 */
export function isOfflineError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 0;
}

// --- Single-flight refresh ---------------------------------------------------

/**
 * The three distinguishable outcomes of a silent refresh (gh-461). Splitting them at the SOURCE is
 * the central fix: boot and the 401 path must treat a genuine expiry (`!res.ok`) and an offline
 * transport failure (`fetch` threw) DIFFERENTLY — the former logs out, the latter must not.
 *  - `ok`      — a fresh token was minted and adopted.
 *  - `expired` — the server refused the refresh (revoked/expired family); the token is cleared.
 *  - `offline` — the request never reached the server; the in-memory token is left UNTOUCHED so a
 *    reload can still restore an offline-limited session and an in-session blip does not log out.
 */
export type RefreshResult =
  | { status: "ok"; token: string }
  | { status: "expired" }
  | { status: "offline" };

let refreshPromise: Promise<RefreshResult> | null = null;

/**
 * Attempt exactly one refresh. Concurrent callers share the same in-flight promise (single-flight),
 * so a burst of 401s triggers only ONE network refresh. Resolves to a discriminated `RefreshResult`.
 *
 * Clears the in-memory token ONLY on a genuine expiry (`!res.ok`) — NEVER on a transport failure
 * (`catch`), which returns `offline` with the token intact (gh-461).
 */
export function refreshAccessToken(): Promise<RefreshResult> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_PREFIX}${REFRESH_PATH}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        setAccessToken(null);
        return { status: "expired" };
      }
      const body = await res.json().catch(() => null);
      const token: string | null = body?.data?.access_token ?? null;
      if (!token) {
        // An ok response with no token is a malformed success — fail closed (clear + expired),
        // matching the pre-split behaviour that returned null here.
        setAccessToken(null);
        return { status: "expired" };
      }
      setAccessToken(token);
      return { status: "ok", token };
    } catch {
      // Transport failure (offline, DNS, CORS). Do NOT clear the token/identity — this is the
      // recoverable case the whole gh-461 fix depends on.
      return { status: "offline" };
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// --- Core request ------------------------------------------------------------

export interface ApiFetchOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * Opt out of the 401 -> silent-refresh -> retry flow for this call. Public,
   * pre-session auth endpoints (login, mfa/send, mfa/verify, /auth/token) MUST set
   * this: there is no session to refresh yet, so a 401 from them is a real auth
   * error (e.g. AUTH-001 bad credentials, AUTH-004 bad/expired OTP) that must
   * surface as an ApiError for the caller to map — not get swallowed into a refresh
   * attempt that fails and throws AuthExpiredError.
   */
  skipAuthRefresh?: boolean;
  /** Internal: marks the one allowed retry after a refresh. Do not set manually. */
  _retried?: boolean;
}

async function unwrap<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;

  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const code = body?.error?.code ?? "UNKNOWN";
    const message = body?.error?.message ?? `Request failed (${res.status})`;
    throw new ApiError(message, code, res.status, body?.error?.details);
  }

  // Success envelope: { status: "success", data: ... }
  if (body && typeof body === "object" && "data" in body) {
    return body.data as T;
  }
  return body as T;
}

export async function apiFetch<T = unknown>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { method = "GET", body, headers = {}, signal, skipAuthRefresh = false, _retried = false } = options;

  // FormData (file uploads, Story 16.3) must NOT be JSON-stringified, and we must
  // NOT set Content-Type — the browser sets `multipart/form-data; boundary=…` itself
  // (a hand-set Content-Type would omit the boundary and the upload would fail to
  // parse). JSON bodies keep the existing behaviour. The Bearer + 401-refresh paths
  // below are untouched.
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  let res: Response;
  try {
    res = await fetch(`${API_PREFIX}${path}`, {
      method,
      credentials: "include",
      signal,
      headers: {
        ...(body !== undefined && !isFormData ? { "Content-Type": "application/json" } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...headers,
      },
      body: body === undefined ? undefined : isFormData ? (body as FormData) : JSON.stringify(body),
    });
  } catch (err) {
    // An aborted request is the caller's own intent — let it propagate untouched.
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    // Network-level failure (offline, DNS, CORS, etc.): fetch rejects with a raw
    // TypeError. Wrap it so callers keep the single ApiError contract (status 0 =
    // request never reached the server).
    throw new ApiError("Network request failed", "NETWORK_ERROR", 0, err);
  }

  // 401 handling — never on the refresh endpoint itself, never for pre-session auth
  // calls that opted out, and at most one retry.
  if (res.status === 401 && path !== REFRESH_PATH && !skipAuthRefresh) {
    if (_retried) {
      // Already retried after a refresh and still 401 — the session is gone.
      setAccessToken(null);
      onAuthExpired?.();
      throw new AuthExpiredError();
    }
    const r = await refreshAccessToken();
    if (r.status === "ok") {
      // Retry the original request exactly once with the refreshed token.
      return apiFetch<T>(path, { ...options, _retried: true });
    }
    if (r.status === "offline") {
      // Transport failure mid-refresh: transient, NOT a logout (gh-461). Surface as a network error
      // the caller handles like any other offline call — no onAuthExpired, no session clear.
      throw new ApiError("Network request failed", "NETWORK_ERROR", 0);
    }
    // r.status === "expired" — genuine session death (refreshAccessToken already cleared the token).
    onAuthExpired?.();
    throw new AuthExpiredError();
  }

  return unwrap<T>(res);
}
